const SHELL_FETCH_TIMEOUT_MS = 5000

const APP_SHELL_CACHE = 'app-shell'
const SHELL_DOCUMENT = '/index.html'

// Caches holding the app's CODE that are safe to purge on every recovery.
// js-cache/css-cache are runtime *fallback* caches — recovery only runs online
// (both callers gate on a reachable server), so the network and the Workbox
// precache repopulate them. Deleting them here drops any stale chunk a partial
// previous load may have cached.
const ALWAYS_PURGE_CACHE_NAMES = ['js-cache', 'css-cache']

// Caches that hold (or back) the app SHELL. Deleting these strands the client
// with no document to render, so they are ONLY purged when we already hold a
// fresh shell in memory to put back — keeping the final cache state monotonic
// (see recoverToLatestBuild). `workbox-precache*` is matched by prefix (Workbox
// suffixes it with a version + origin). Content caches — `media-uploads-cache`
// (prefetched attachments), `image-cache`, `font-cache` — are deliberately
// absent: field users prefetch photos and a version bump must not wipe them.
const SHELL_BEARING_CACHE_NAMES = [APP_SHELL_CACHE]
const SHELL_BEARING_CACHE_PREFIXES = ['workbox-precache']

interface RecoverToLatestBuildOptions {
  reloadParam: string
  logPrefix: string
}

interface RecoverIfShellInstalledOptions extends RecoverToLatestBuildOptions {
  /**
   * Runs once the fresh shell is installed, before any cache is purged or the page replaced.
   * Returning false cancels the reload — the loop guard uses it to refuse a reload it could not
   * record (buildReloadGuard.ts).
   */
  beforeReload?: () => boolean
}

export type ShellRecoveryResult =
  | { reloaded: true }
  | { reloaded: false; reason: 'no-fresh-shell' | 'shell-not-installed' | 'reload-cancelled' }

export function buildReloadUrl(
  currentUrl: string,
  reloadParam: string,
  markerValue: number | string,
) {
  const nextUrl = new URL(currentUrl)
  nextUrl.searchParams.set(reloadParam, String(markerValue))
  return nextUrl.toString()
}

/**
 * Decide which Cache Storage entries a recovery should delete, given the full
 * list of cache names. Pure (no `caches` access) so the allowlist is unit
 * testable, mirroring the codebase's `*Decisions.ts` helpers.
 *
 * - app-code fallback caches (js-cache, css-cache) → always purged.
 * - shell-bearing caches (app-shell, workbox-precache*) → purged ONLY when
 *   `purgeShellCaches` is true (i.e. a fresh shell is in hand to repopulate).
 * - everything else (media/image/font, plus any cache we don't recognise) → kept.
 */
export function selectCachesToPurge(
  cacheNames: string[],
  { purgeShellCaches }: { purgeShellCaches: boolean },
): string[] {
  return cacheNames.filter(name => {
    if (ALWAYS_PURGE_CACHE_NAMES.includes(name)) return true
    if (!purgeShellCaches) return false
    if (SHELL_BEARING_CACHE_NAMES.includes(name)) return true
    return SHELL_BEARING_CACHE_PREFIXES.some(prefix => name.startsWith(prefix))
  })
}

/**
 * Fetch the latest app shell, bypassing the HTTP cache, and HOLD it in memory.
 * The returned Response body is left unread so the caller can `put()` it into
 * the app-shell cache. Timeout-bounded so a hung/half-open network can't
 * dead-lock the whole recovery sequence. Returns null on any failure — the
 * caller then preserves the existing shell rather than stranding the client.
 */
async function fetchFreshShell(logPrefix: string): Promise<Response | null> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), SHELL_FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(SHELL_DOCUMENT, {
      credentials: 'same-origin',
      cache: 'reload',
      signal: controller.signal,
    })
    if (response.ok) return response
    console.warn(
      `[${logPrefix}] Fresh shell fetch returned ${response.status}; preserving existing shell`,
    )
    return null
  } catch (error) {
    console.warn(`[${logPrefix}] Fresh shell fetch failed; preserving existing shell`, error)
    return null
  } finally {
    window.clearTimeout(timeoutId)
  }
}

/**
 * Delete app-CODE caches by allowlist. Content caches (media/image/font) and
 * any unrecognised cache are preserved. Shell-bearing caches are purged only
 * when `purgeShellCaches` is true — the caller passes true only when it holds a
 * fresh shell to repopulate, so the client is never left without a document.
 */
async function clearAppCodeCaches(
  logPrefix: string,
  purgeShellCaches: boolean,
  keep: readonly string[] = [],
) {
  if (!('caches' in window)) return

  try {
    const keys = await caches.keys()
    const toDelete = selectCachesToPurge(keys, { purgeShellCaches }).filter(
      key => !keep.includes(key),
    )
    await Promise.all(toDelete.map(key => caches.delete(key)))
    console.log(`[${logPrefix}] Cleared app-code caches: ${toDelete.join(', ') || '(none)'}`)
  } catch (error) {
    console.error(`[${logPrefix}] Failed to clear app-code caches:`, error)
  }
}

/**
 * Put the freshly-fetched shell back into the app-shell runtime cache. The SW's
 * navigation route checks this cache before the Workbox precache, so the next
 * navigation (and any offline reload) renders this known-good document even if
 * the precache was purged or the SW update did not land.
 */
async function repopulateAppShell(logPrefix: string, freshShell: Response): Promise<boolean> {
  // No Cache API means no cache could serve an old shell either: the reload goes to the
  // network, which just answered with the fresh one. Counted as installed.
  if (!('caches' in window)) return true

  try {
    const cache = await caches.open(APP_SHELL_CACHE)
    await cache.put(SHELL_DOCUMENT, freshShell)
    console.log(`[${logPrefix}] Repopulated ${APP_SHELL_CACHE} with fresh ${SHELL_DOCUMENT}`)
    return true
  } catch (error) {
    console.error(`[${logPrefix}] Failed to repopulate app-shell:`, error)
    return false
  }
}

// Kick the service worker to fetch + install the latest worker. FIRE-AND-FORGET
// by design — we deliberately do NOT await it. On Safari/WebKit
// registration.update() can hang for seconds; awaiting it (even with a timeout)
// made the toast's "Reload" button feel dead — the page only refreshed once the
// await gave up. There is nothing to wait for: the forced reload below
// revalidates the worker anyway (registration uses updateViaCache: 'none'), and
// the network-first navigation route serves the fresh shell regardless of which
// worker is currently active. The new worker then activates on the next load.
function kickServiceWorkerUpdate(logPrefix: string) {
  if (!('serviceWorker' in navigator)) return

  void navigator.serviceWorker
    .getRegistration()
    .then(registration => registration?.update())
    .then(
      () => console.log(`[${logPrefix}] SW update kicked`),
      error => console.error(`[${logPrefix}] SW update kick failed:`, error),
    )
}

/**
 * Recover a client to the latest build without losing offline-first data.
 *
 * The sequence is MONOTONIC — the final cache state always contains a
 * known-good shell:
 *   1. Fetch the latest /index.html and HOLD it in memory (do NOT cache it yet;
 *      step 3 may delete the app-shell cache).
 *   2. Kick the SW update in the BACKGROUND (fire-and-forget) — never block the
 *      reload on it (Safari's registration.update() can hang for seconds).
 *   3. Purge stale app-code caches by allowlist — preserving media/image/font.
 *      The shell-bearing caches (app-shell, workbox-precache*) are purged ONLY
 *      when step 1 produced a fresh shell to put back.
 *   4. Recreate the app-shell cache with that fresh shell.
 *   5. Reload to the latest build.
 *
 * Without the hold-in-memory → delete → recreate ordering, step 3 would delete
 * the very shell step 1 just fetched.
 */
export async function recoverToLatestBuild({
  reloadParam,
  logPrefix,
}: RecoverToLatestBuildOptions) {
  console.log(`[${logPrefix}] Starting reload sequence`)

  // 1. Grab the latest shell and hold it in memory before touching any cache.
  const freshShell = await fetchFreshShell(logPrefix)

  // 2. Kick the SW update in the background — do NOT block the reload on it.
  kickServiceWorkerUpdate(logPrefix)

  // 3. Purge stale app-code caches. Only purge the shell-bearing caches when we
  //    actually hold a fresh shell to put back — otherwise we'd strand the
  //    client with no document (non-monotonic).
  await clearAppCodeCaches(logPrefix, freshShell !== null)

  // 4. Recreate the app-shell with the fresh shell so the post-reload
  //    navigation — and any offline reload — always finds a known-good document.
  if (freshShell) {
    await repopulateAppShell(logPrefix, freshShell)
  }

  // 5. Reload to the latest build.
  const reloadUrl = buildReloadUrl(window.location.href, reloadParam, Date.now())
  console.log(`[${logPrefix}] Reloading to:`, reloadUrl)
  window.location.replace(reloadUrl)
}

/**
 * The same recovery, but it RELOADS ONLY when a fresh shell was fetched AND installed, and
 * otherwise leaves the client exactly as it was and says why.
 *
 * For the reloads nobody asked for (startup, a required update): recoverToLatestBuild above
 * reloads even without a shell, which is right for a Reload button the user just pressed and
 * wrong for a reload the app decides on its own — offline or on a half-open link that reload
 * lands on whatever the caches still hold, very likely the old build again, and the user loses
 * the page for nothing.
 *
 * Ordering differs from recoverToLatestBuild for the same monotonic reason: the shell is put
 * into app-shell FIRST (which overwrites the one entry that cache holds), and only then are the
 * other app-code caches purged, keeping app-shell. A failed install therefore returns before
 * anything is deleted, and the client keeps every cache it had. A cancelled reload returns at the
 * same point: the only change it leaves behind is the fresh shell in app-shell.
 */
export async function recoverToLatestBuildIfShellInstalled({
  reloadParam,
  logPrefix,
  beforeReload,
}: RecoverIfShellInstalledOptions): Promise<ShellRecoveryResult> {
  console.log(`[${logPrefix}] Starting guarded reload sequence`)

  const freshShell = await fetchFreshShell(logPrefix)
  if (!freshShell) return { reloaded: false, reason: 'no-fresh-shell' }

  if (!(await repopulateAppShell(logPrefix, freshShell))) {
    return { reloaded: false, reason: 'shell-not-installed' }
  }

  if (beforeReload && !beforeReload()) return { reloaded: false, reason: 'reload-cancelled' }

  kickServiceWorkerUpdate(logPrefix)
  await clearAppCodeCaches(logPrefix, true, [APP_SHELL_CACHE])

  const reloadUrl = buildReloadUrl(window.location.href, reloadParam, Date.now())
  console.log(`[${logPrefix}] Reloading to:`, reloadUrl)
  window.location.replace(reloadUrl)
  return { reloaded: true }
}
